// Verbindungsstand der Oberfläche: Browser online?, Server erreichbar?, Echtzeit-Kanal verbunden?
// Speist die Leiste „Keine Verbindung“ unter der Topbar. Der Echtzeit-Kanal ist ein Zusatz: Fehlt er ganz
// ('unavailable'), arbeitet die Oberfläche still ohne ihn weiter (Abfragen laden beim Fokuswechsel neu).
import { create } from 'zustand'

export type RealtimeState =
  /** noch nicht versucht (nicht angemeldet) */
  | 'idle'
  | 'connecting'
  | 'online'
  /** war verbunden und ist abgerissen – die Daten können veraltet sein */
  | 'offline'
  /** der Server bietet (noch) keinen Echtzeit-Kanal an */
  | 'unavailable'

export interface ConnectionState {
  browserOnline: boolean
  /** false, sobald eine Anfrage den Server nicht erreicht hat; true nach der nächsten beantworteten */
  apiReachable: boolean
  realtime: RealtimeState
  /**
   * Gerade aus dem Hintergrund zurück (App-Wechsel, Tab wieder sichtbar): Der Echtzeit-Kanal baut sich neu auf. Für
   * RESUME_GRACE_MS zählt sein Abriss nicht als „keine Verbindung“ – sonst blitzt die Leiste bei jedem Wechsel auf.
   * Eine gescheiterte Anfrage zeigt sie trotzdem sofort (docs/mobile/SPEZIFIKATION.md §4.9).
   */
  resuming: boolean
}

export const RESUME_GRACE_MS = 3000

export const useConnectionStore = create<ConnectionState>(() => ({
  browserOnline: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  apiReachable: true,
  realtime: 'idle',
  resuming: false,
}))

export const isOffline = (state: ConnectionState): boolean =>
  !state.browserOnline || !state.apiReachable || (state.realtime === 'offline' && !state.resuming)

export const useIsOffline = (): boolean => useConnectionStore(isOffline)

export const setRealtimeState = (realtime: RealtimeState): void => useConnectionStore.setState({ realtime })
export const setApiReachable = (apiReachable: boolean): void => {
  if (useConnectionStore.getState().apiReachable !== apiReachable) useConnectionStore.setState({ apiReachable })
}

/** Folgt den online/offline-Ereignissen des Browsers. Gibt eine Aufräumfunktion zurück. */
export function watchBrowserConnection(): () => void {
  if (typeof window === 'undefined') return () => undefined
  const update = (): void => useConnectionStore.setState({ browserOnline: navigator.onLine !== false })
  update()
  let graceTimer: ReturnType<typeof setTimeout> | undefined
  const onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return
    clearTimeout(graceTimer)
    useConnectionStore.setState({ resuming: true })
    graceTimer = setTimeout(() => useConnectionStore.setState({ resuming: false }), RESUME_GRACE_MS)
  }
  window.addEventListener('online', update)
  window.addEventListener('offline', update)
  document.addEventListener('visibilitychange', onVisibility)
  return () => {
    clearTimeout(graceTimer)
    window.removeEventListener('online', update)
    window.removeEventListener('offline', update)
    document.removeEventListener('visibilitychange', onVisibility)
    useConnectionStore.setState({ resuming: false })
  }
}
